import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PRIMARY_BTN_CLS } from "@/components/premium";

const MIN_LENGTH = 8;

export default function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Live, friendly validation. Backend re-validates everything.
  const errors = {};
  if (next && next.length < MIN_LENGTH) errors.next = `Minst ${MIN_LENGTH} tegn`;
  if (confirm && confirm !== next) errors.confirm = "Passordene er ikke like";
  const canSubmit =
    !submitting &&
    current.length > 0 &&
    next.length >= MIN_LENGTH &&
    confirm === next &&
    next !== current;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Passord oppdatert");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kunne ikke oppdatere passord");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="rounded-2xl border-border" data-testid="change-password-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <KeyRound className="w-5 h-5" strokeWidth={1.75} />
          Endre passord
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4 max-w-md" noValidate>
          <div>
            <Label htmlFor="cp-current">Nåværende passord</Label>
            <PasswordInput
              id="cp-current"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="rounded-xl mt-1.5"
              data-testid="cp-current-input"
              toggleTestId="cp-current-toggle"
            />
          </div>

          <div>
            <Label htmlFor="cp-new">Nytt passord</Label>
            <PasswordInput
              id="cp-new"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="rounded-xl mt-1.5"
              data-testid="cp-new-input"
              toggleTestId="cp-toggle-visibility"
            />
            {errors.next ? (
              <p className="text-xs text-destructive mt-1" data-testid="cp-new-error">{errors.next}</p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">Minst {MIN_LENGTH} tegn.</p>
            )}
          </div>

          <div>
            <Label htmlFor="cp-confirm">Bekreft nytt passord</Label>
            <PasswordInput
              id="cp-confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="rounded-xl mt-1.5"
              data-testid="cp-confirm-input"
              toggleTestId="cp-confirm-toggle"
            />
            {errors.confirm && (
              <p className="text-xs text-destructive mt-1" data-testid="cp-confirm-error">{errors.confirm}</p>
            )}
          </div>

          <Button
            type="submit"
            className={PRIMARY_BTN_CLS}
            disabled={!canSubmit}
            data-testid="cp-submit-btn"
          >
            {submitting ? "Oppdaterer..." : "Oppdater passord"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
